'use client'

import type {
  CrossTabLockOptions,
  CrossTabLockResult,
} from '@maestro/commerce-blocks/hooks/hooks.types'

import { useCallback, useRef } from 'react'

import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:commerce-blocks:cross-tab-lock')

const isBrowser = (): boolean => typeof window !== 'undefined'

const isWebLocksAvailable = (): boolean => isBrowser() && 'locks' in navigator

/**
 * localStorage-based lock with TTL as fallback when Web Locks API is unavailable.
 * Best-effort: read→write→verify is not truly atomic, but sufficient for most cases.
 */
class LocalStorageLock {
  constructor(
    private readonly key: string,
    private readonly ttlMs: number
  ) {}

  acquire(): boolean {
    try {
      const existing = localStorage.getItem(this.key)
      if (existing) {
        const expiry = parseInt(existing, 10)
        if (expiry > Date.now()) {
          return false
        }
      }

      const expiry = (Date.now() + this.ttlMs).toString()
      localStorage.setItem(this.key, expiry)

      return localStorage.getItem(this.key) === expiry
    } catch {
      return false
    }
  }

  release(): void {
    try {
      localStorage.removeItem(this.key)
    } catch {
      // ignore
    }
  }
}

async function withWebLock<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T | undefined> {
  let result: T | undefined

  // `ifAvailable` makes this non-blocking: returns immediately if another tab holds the lock
  await navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
    if (!lock) {
      log.debug('Web Lock not available, another tab holds it', { name })
      return
    }
    result = await fn()
  })

  return result
}

async function withLocalStorageLock<T>(
  lock: LocalStorageLock,
  fn: () => Promise<T>
): Promise<T | undefined> {
  if (!lock.acquire()) {
    log.debug('localStorage lock not acquired, another tab holds it')
    return undefined
  }

  try {
    return await fn()
  } finally {
    lock.release()
  }
}

/**
 * Cross-tab mutual exclusion hook.
 *
 * Uses the Web Locks API when available (true mutual exclusion across tabs),
 * falling back to a localStorage-based lock with TTL otherwise.
 */
export const useCrossTabLock = (
  props: CrossTabLockOptions
): CrossTabLockResult => {
  const { name, ttlMs = 10_000 } = props
  const localLockRef = useRef<LocalStorageLock | null>(null)

  const getLocalLock = useCallback(() => {
    if (!localLockRef.current) {
      localLockRef.current = new LocalStorageLock(name, ttlMs)
    }
    return localLockRef.current
  }, [name, ttlMs])

  const withLock = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
      if (!isBrowser()) {
        log.warn('withLock called during SSR, executing without lock')
        return fn()
      }
      if (isWebLocksAvailable()) {
        return withWebLock(name, fn)
      }
      return withLocalStorageLock(getLocalLock(), fn)
    },
    [name, getLocalLock]
  )

  return { withLock }
}
